import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiArrowLeft, FiLoader, FiSave, FiTrash2 } from "react-icons/fi";
import { CountdownTimer } from "teamsite/src/pages/NewAiDesign/components/utils";
import { HandleAxiosPromise } from "@shared_components/axios/HandleAxiosPromise";
import { useMessage } from "@shared_components/providers/MessageProvider";
import { AiDesignStudioWrapper, AiDesignStudioWrapperRef } from "@shared_components/mainsite_pages/aidesign/AiDesignStudio";
import { useGearStudioDesign } from "@src/providers/GearstudioDesignProvider";
import {
    GEARSTUDIO_CALCULATE_CAPACITY_URL,
    GEARSTUDIO_CREATE_ORDER_URL,
    GEARSTUDIO_EDIT_PROMPT_URL,
    GEARSTUDIO_HANDLING_FEE_URL,
    GEARSTUDIO_PANEL_IMAGES_URL,
    GearStudioDesign,
    GearStudioImage,
    GearStudioPanel,
    GearstudioPricing,
    GearstudioUserImage,
    HandlingFeeRange,
} from "@src/types";
import { formatCreatedAt } from "../components/gearstudiobody.utils";
import { GearstudioAuthPanel } from "../components/gearstudioauthpanel";
import { ImageManagerModal } from "../components/ImageManagerModal";
import {
    getImageSrc,
    getRevisionsFromPayload,
    mergeDesignWithSourcePanelImages,
    mergeIncomingWithExistingDesigns,
    sortDesignsByRevision,
} from "../components/editdesignview.utils";
import { FaWindowClose } from "react-icons/fa";
import { TooltipElement } from "@shared_components/components/Tooltip";
import { CanvasToolbarProvider } from "../../../adminportal/src/pages/ProductManagement/SavedDesign/providers/CanvasToolbarProvider";
import { LogoEditor2D } from "@shared_components/mainsite_pages/aidesign/LogoEditor2D";
import { useMobileCheck } from "@shared_components/functions/mobileCheck";

type EditDesignViewProps = {
    loggedIn: boolean;
    user_uuid: string;
};

type CapacityOption = {
    date?: string | null;
};

const TEXTAREA_CLASSNAME = "gs-input min-h-[168px] p-3 text-sm";
const SELECT_CONTROL_CLASSNAME = "gs-input px-3 py-2.5 text-sm";
const USD_CURRENCY_FORMATTER = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

const toPriceNumber = (value: unknown): number => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return 0;
    }
    return Number(parsed.toFixed(2));
};

const normalizePricing = (payload: unknown): GearstudioPricing | null => {
    if (!payload || typeof payload !== "object") {
        return null;
    }
    const raw = payload as Partial<GearstudioPricing>;
    return {
        product_price: toPriceNumber(raw.product_price),
        processing_fee: toPriceNumber(raw.processing_fee),
        total_price: toPriceNumber(raw.total_price),
    };
};

const normalizeHandlingFees = (payload: unknown): HandlingFeeRange[] => {
    if (!Array.isArray(payload)) {
        return [];
    }
    return payload
        .map((fee) => {
            const raw = fee as Partial<HandlingFeeRange>;
            return {
                fee_amount: toPriceNumber(raw.fee_amount),
                min_order_value: toPriceNumber(raw.min_order_value),
                max_order_value: toPriceNumber(raw.max_order_value),
            };
        })
        .sort((left, right) => left.min_order_value - right.min_order_value);
};

const getCapacityStandardOption = (payload: unknown): CapacityOption | null => {
    if (Array.isArray(payload)) {
        const first = payload[0];
        if (first && typeof first === "object") {
            return first as CapacityOption;
        }
        return null;
    }

    if (!payload || typeof payload !== "object") {
        return null;
    }

    const rawPayload = payload as { standard?: unknown; data?: unknown };
    if (rawPayload.standard && typeof rawPayload.standard === "object") {
        return rawPayload.standard as CapacityOption;
    }

    const nestedData = rawPayload.data;
    if (Array.isArray(nestedData)) {
        const first = nestedData[0];
        if (first && typeof first === "object") {
            return first as CapacityOption;
        }
        return null;
    }

    if (nestedData && typeof nestedData === "object") {
        const nested = nestedData as { standard?: unknown };
        if (nested.standard && typeof nested.standard === "object") {
            return nested.standard as CapacityOption;
        }
    }

    return null;
};

const getStandardDeliveryDate = (payload: unknown): string | null => {
    const standardOption = getCapacityStandardOption(payload);
    if (!standardOption) {
        return null;
    }

    const date = standardOption.date;
    if (typeof date !== "string" || date.trim() === "") {
        return null;
    }

    return date;
};

const formatDeliveryDate = (rawDate: string): string => {
    const normalizedDate = rawDate.includes("T") ? rawDate : `${rawDate}T00:00:00`;
    const parsedDate = new Date(normalizedDate);
    if (Number.isNaN(parsedDate.getTime())) {
        return rawDate;
    }
    return parsedDate.toLocaleDateString();
};

export const EditDesignView = ({ loggedIn, user_uuid }: EditDesignViewProps) => {
    const { designId } = useParams<{ designId: string }>();
    const { setTextData } = useMessage();
    const { usageData, fetchUsageData, current_design, setCurrentDesign } = useGearStudioDesign();

    const [loadingDesign, setLoadingDesign] = useState(true);
    const [sendingRequest, setSendingRequest] = useState(false);
    const [creatingOrder, setCreatingOrder] = useState(false);
    const [orderQty, setOrderQty] = useState(1);
    const [designs, setDesigns] = useState<GearStudioDesign[]>([]);
    const [selectedDesignId, setSelectedDesignId] = useState<number | null>(null);
    const [newPrompt, setNewPrompt] = useState("");

    const [selectedPanelId, setSelectedPanelId] = useState<number | null>(null);
    const [selectedImageId, setSelectedImageId] = useState<number | null>(null);
    const [showImageManager, setShowImageManager] = useState(false);
    const [showImageEditor, setShowImageEditor] = useState(false);
    const [apiPricing, setApiPricing] = useState<GearstudioPricing | null>(null);
    const [handlingFees, setHandlingFees] = useState<HandlingFeeRange[]>([]);
    const [standardDeliveryDate, setStandardDeliveryDate] = useState<string | null>(null);
    const [loadingStandardDeliveryDate, setLoadingStandardDeliveryDate] = useState(false);

    const designsRef = useRef<GearStudioDesign[]>([]);
    const nextTempImageIdRef = useRef(-1);
    const previewContainerRef = useRef<HTMLDivElement | null>(null);
    const studioRef = useRef<AiDesignStudioWrapperRef | null>(null);
    const isMobile = useMobileCheck();


    const livePanel = useMemo(() => {
        if (!current_design || current_design.panels.length === 0) {
            return null;
        }
        if (!selectedPanelId) {
            return current_design.panels[0];
        }
        return current_design.panels.find((panel) => panel.id === selectedPanelId) || current_design.panels[0];
    }, [current_design, selectedPanelId]);

    const selectedPanelImage = useMemo(() => {
        if (!livePanel || !selectedImageId) {
            return null;
        }
        return livePanel.images.find((image) => image.id === selectedImageId) || null;
    }, [livePanel, selectedImageId]);

    const revisionDisplayNumberById = useMemo(() => {
        const mapping = new Map<number, number>();
        sortDesignsByRevision(designs).forEach((revision, index) => {
            mapping.set(revision.id, index + 1);
        });
        return mapping;
    }, [designs]);

    const productUnitPrice = useMemo(() => {
        const fromDesign = Number(current_design?.product_template?.product_price);
        if (Number.isFinite(fromDesign)) {
            return Number(fromDesign.toFixed(2));
        }
        if (apiPricing) {
            return apiPricing.product_price;
        }
        return 0;
    }, [apiPricing, current_design?.product_template?.product_price]);

    const pricingSummary = useMemo(() => {
        const quantity = Math.max(1, Number(orderQty) || 1);
        const subtotal = toPriceNumber(productUnitPrice * quantity);
        const matchingFee = handlingFees.find(
            (fee) => subtotal >= fee.min_order_value && subtotal <= fee.max_order_value,
        );
        const processingFee = matchingFee
            ? matchingFee.fee_amount
            : toPriceNumber(apiPricing?.processing_fee || 0);
        const total = toPriceNumber(subtotal + processingFee);

        return {
            subtotal,
            processingFee,
            total,
        };
    }, [apiPricing?.processing_fee, handlingFees, orderQty, productUnitPrice]);

    useEffect(() => {
        if (!loggedIn || !user_uuid) {
            return;
        }

        fetchUsageData(user_uuid, { showLoading: false });
        const interval = setInterval(() => {
            fetchUsageData(user_uuid, { showLoading: false });
        }, 60000);

        return () => clearInterval(interval);
    }, [fetchUsageData, loggedIn, user_uuid]);

    useEffect(() => {
        if (!loggedIn) {
            setHandlingFees([]);
            return;
        }

        let mounted = true;
        const fetchHandlingFees = async () => {
            try {
                const response = await HandleAxiosPromise({
                    url: GEARSTUDIO_HANDLING_FEE_URL,
                    method: "get",
                });
                if (!mounted) {
                    return;
                }
                setHandlingFees(normalizeHandlingFees(response?.data?.data));
            } catch (_error) {
                if (!mounted) {
                    return;
                }
                setHandlingFees([]);
            }
        };

        fetchHandlingFees();
        return () => {
            mounted = false;
        };
    }, [loggedIn]);

    useEffect(() => {
        if (!loggedIn || !current_design) {
            setStandardDeliveryDate(null);
            setLoadingStandardDeliveryDate(false);
            return;
        }

        let mounted = true;
        const qty = Math.max(1, Math.min(999, Number(orderQty) || 1));
        const startDate = new Date().toISOString().split("T")[0];

        setLoadingStandardDeliveryDate(true);
        HandleAxiosPromise({
            url: GEARSTUDIO_CALCULATE_CAPACITY_URL,
            method: "post",
            data: {
                start_date: startDate,
                qty,
                standard_only: true,
            },
        })
            .then((response) => {
                if (!mounted) {
                    return;
                }
                setStandardDeliveryDate(getStandardDeliveryDate(response?.data));
            })
            .catch(() => {
                if (!mounted) {
                    return;
                }
                setStandardDeliveryDate(null);
            })
            .finally(() => {
                if (!mounted) {
                    return;
                }
                setLoadingStandardDeliveryDate(false);
            });

        return () => {
            mounted = false;
        };
    }, [current_design?.id, loggedIn, orderQty]);

    useEffect(() => {
        designsRef.current = designs;
    }, [designs]);

    useEffect(() => {
        if (designs.length === 0) {
            setCurrentDesign(null);
            return;
        }

        const nextCurrentDesign = (
            selectedDesignId
                ? designs.find((design) => design.id === selectedDesignId)
                : designs[designs.length - 1]
        ) || designs[designs.length - 1];

        setCurrentDesign((previous) => (previous === nextCurrentDesign ? previous : nextCurrentDesign));
    }, [designs, selectedDesignId, setCurrentDesign]);

    const updateCurrentDesign = useCallback((updater: (design: GearStudioDesign) => GearStudioDesign) => {
        if (!current_design) {
            return;
        }

        const currentDesignId = current_design.id;
        setCurrentDesign((previous) => {
            if (!previous || previous.id !== currentDesignId) {
                return previous;
            }
            return updater(previous);
        });

        setDesigns((previous) =>
            previous.map((design) => (design.id === currentDesignId ? updater(design) : design)),
        );
    }, [current_design, setCurrentDesign]);

    const updateLivePanel = useCallback((updater: (panel: GearStudioPanel) => GearStudioPanel) => {
        if (!livePanel) {
            return;
        }

        updateCurrentDesign((design) => ({
            ...design,
            panels: design.panels.map((panel) => (panel.id === livePanel.id ? updater(panel) : panel)),
        }));
    }, [livePanel, updateCurrentDesign]);

    const updatePanelImage = useCallback((imageId: number, patch: Partial<GearStudioImage>) => {
        updateLivePanel((panel) => ({
            ...panel,
            images: panel.images.map((image) => (image.id === imageId ? { ...image, ...patch } : image)),
        }));
    }, [updateLivePanel]);

    const fetchDesign = useCallback(async () => {
        if (!loggedIn || !user_uuid) {
            setLoadingDesign(false);
            setDesigns([]);
            setSelectedDesignId(null);
            setCurrentDesign(null);
            setApiPricing(null);
            return;
        }

        const parsedDesignId = Number(designId);
        if (!designId || Number.isNaN(parsedDesignId) || parsedDesignId <= 0) {
            setTextData({ text: "Invalid design id.", variant: "danger" });
            setLoadingDesign(false);
            setDesigns([]);
            setSelectedDesignId(null);
            setCurrentDesign(null);
            setApiPricing(null);
            return;
        }

        setLoadingDesign(true);
        try {
            const response = await HandleAxiosPromise({
                url: GEARSTUDIO_EDIT_PROMPT_URL,
                method: "get",
                data: { design_id: parsedDesignId },
                additional_headers: { "User-Id": user_uuid },
            });

            const payload = response?.data || {};
            const incomingRevisions = getRevisionsFromPayload(payload);
            if (incomingRevisions.length === 0) {
                setTextData({ text: "Design not found.", variant: "danger" });
                setDesigns([]);
                setSelectedDesignId(null);
                setCurrentDesign(null);
                setApiPricing(null);
                return;
            }

            const nextDesigns = mergeIncomingWithExistingDesigns(incomingRevisions, designsRef.current);
            const selectedDesign = nextDesigns.find((design) => design.id === parsedDesignId);
            const payloadDesign = payload?.data as GearStudioDesign | undefined;
            const nextSelectedDesignId = selectedDesign?.id || payloadDesign?.id || nextDesigns[nextDesigns.length - 1].id;

            const sortedDesigns = sortDesignsByRevision(nextDesigns);
            setDesigns(sortedDesigns);
            setSelectedDesignId(nextSelectedDesignId);
            setApiPricing(normalizePricing(payload?.pricing));

            const nextCurrent = sortedDesigns.find((design) => design.id === nextSelectedDesignId) || sortedDesigns[sortedDesigns.length - 1];
            setCurrentDesign(nextCurrent || null);
        } catch (error) {
            const message = typeof error === "string" ? error : "Failed to fetch design data.";
            setTextData({ text: message, variant: "danger" });
            setDesigns([]);
            setSelectedDesignId(null);
            setCurrentDesign(null);
            setApiPricing(null);
        } finally {
            setLoadingDesign(false);
        }
    }, [designId, loggedIn, setCurrentDesign, setTextData, user_uuid]);

    useEffect(() => {
        fetchDesign();
    }, [fetchDesign]);

    useEffect(() => {
        if (!current_design) {
            setSelectedPanelId(null);
            setSelectedImageId(null);
            setShowImageEditor(false);
            return;
        }

        setSelectedPanelId((previous) => {
            if (previous && current_design.panels.some((panel) => panel.id === previous)) {
                return previous;
            }
            return current_design.panels[0]?.id || null;
        });
        setSelectedImageId(null);
        setShowImageEditor(false);
    }, [current_design?.id]);

    useEffect(() => {
        if (!livePanel) {
            setSelectedImageId(null);
            return;
        }

        if (!selectedImageId || !livePanel.images.some((image) => image.id === selectedImageId)) {
            setSelectedImageId(livePanel.images[0]?.id || null);
        }
    }, [livePanel, selectedImageId]);

    const handleSubmitNewPrompt = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!current_design || !loggedIn || !user_uuid) {
            return;
        }

        const trimmedPrompt = newPrompt.trim();
        if (!trimmedPrompt) {
            return;
        }

        setSendingRequest(true);
        try {
            const response = await HandleAxiosPromise({
                url: GEARSTUDIO_EDIT_PROMPT_URL,
                method: "post",
                data: {
                    design_id: current_design.id,
                    user_prompt: trimmedPrompt,
                },
                additional_headers: { "User-Id": user_uuid },
            });

            const payload = response?.data || {};
            const createdDesign = payload?.data as GearStudioDesign | undefined;
            if (!createdDesign) {
                setTextData({ text: "No design was generated. Try another prompt.", variant: "warning" });
                return;
            }

            const createdWithImages = mergeDesignWithSourcePanelImages(createdDesign, current_design);
            const incomingRevisions = getRevisionsFromPayload(payload);
            let nextDesigns: GearStudioDesign[];

            if (incomingRevisions.length > 0) {
                nextDesigns = mergeIncomingWithExistingDesigns(incomingRevisions, designs).map((design) =>
                    design.id === createdWithImages.id ? createdWithImages : design,
                );
            } else {
                nextDesigns = [
                    ...designs.filter((design) => design.id !== createdWithImages.id),
                    createdWithImages,
                ];
            }

            const sortedDesigns = sortDesignsByRevision(nextDesigns);
            setDesigns(sortedDesigns);
            setSelectedDesignId(createdWithImages.id);
            setCurrentDesign(createdWithImages);
            setApiPricing(normalizePricing(payload?.pricing));
            setNewPrompt("");
            setTextData({ text: "New design version generated.", variant: "success" });
        } catch (error) {
            const message = typeof error === "string" ? error : "Failed to generate a new design version.";
            setTextData({ text: message, variant: "danger" });
        } finally {
            setSendingRequest(false);
            fetchUsageData(user_uuid, { showLoading: false });
        }
    };

    const handleRevisionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const nextDesignId = Number(event.target.value);
        if (Number.isNaN(nextDesignId) || nextDesignId <= 0) {
            return;
        }
        setSelectedDesignId(nextDesignId);
    };

    const handlePanelChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const nextPanelId = Number(event.target.value);
        if (Number.isNaN(nextPanelId) || nextPanelId <= 0) {
            return;
        }
        setSelectedPanelId(nextPanelId);
        setSelectedImageId(null);
        setShowImageEditor(false);
    };

    const handleAddImageToPanel = async (asset: GearstudioUserImage) => {
        if (!livePanel) {
            return;
        }

        const panelWidth = livePanel.images_data.width || 1024;
        const imageWidth = asset.images_data.width || 220;
        const imageHeight = asset.images_data.height || 220;

        const baseScale = Math.min(1, (panelWidth * 0.24) / Math.max(1, imageWidth));
        const xPosition = Math.round((panelWidth - imageWidth * baseScale) / 2);
        const yPosition = 0;

        const newImagePayload: GearStudioImage = {
            id: nextTempImageIdRef.current--,
            images_data: {
                src: asset.images_data.src,
                webp: asset.images_data.webp || null,
                width: imageWidth,
                height: imageHeight,
            },
            x_position: Math.max(0, xPosition),
            y_position: Math.max(0, yPosition),
            scale: baseScale,
            rotation: 0,
            source_user_image_id: asset.id,
        };

        const patchPayload = {
            panel_id: livePanel.id,
            images: [...livePanel.images, newImagePayload].map((image) => ({
                id: image.id,
                x_position: image.x_position,
                y_position: image.y_position,
                scale: image.scale,
                rotation: image.rotation,
                source_user_image_id: image.source_user_image_id || null,
                width: image.images_data.width || 0,
                height: image.images_data.height || 0,
            })),
        };

        try {
            const response = await HandleAxiosPromise({
                url: GEARSTUDIO_PANEL_IMAGES_URL,
                method: "patch",
                data: patchPayload,
                additional_headers: { "User-Id": user_uuid },
            });

            if (response.status === 200 && response.data) {
                const updatedPanel = response.data as GearStudioPanel;
                updateLivePanel(() => updatedPanel);
                const matchingSourceImages = updatedPanel.images
                    .filter((image) => image.source_user_image_id === asset.id)
                    .sort((left, right) => right.id - left.id);
                const newestImage = [...updatedPanel.images].sort((left, right) => right.id - left.id)[0];
                setSelectedImageId(matchingSourceImages[0]?.id || newestImage?.id || null);
                setShowImageEditor(true);
                setTextData({ text: "Image added to panel.", variant: "success" });
            } else {
                setTextData({ text: "Failed to add image to panel.", variant: "danger" });
            }
        } catch (error) {
            console.error(error);
            setTextData({ text: "Failed to add image to panel.", variant: "danger" });
        }
    };

    const handleDeletePanelImage = (imageId: number) => {
        updateLivePanel((panel) => ({
            ...panel,
            images: panel.images.filter((image) => image.id !== imageId),
        }));

        if (selectedImageId === imageId) {
            setSelectedImageId(null);
        }
    };

    const getPreviewImageData = useCallback(async () => {
        const controlledCapture = await studioRef.current?.captureInitialViewPng();
        if (controlledCapture) {
            return controlledCapture;
        }

        const container = previewContainerRef.current;
        if (!container) {
            return null;
        }
        const canvas = (
            container.querySelector(".canvas-container canvas") ||
            container.querySelector("canvas")
        ) as HTMLCanvasElement | null;
        if (!canvas) {
            return null;
        }
        try {
            if (canvas.width <= 0 || canvas.height <= 0) {
                return null;
            }
            return canvas.toDataURL("image/png");
        } catch (_error) {
            return null;
        }
    }, []);

    const handleCreateOrder = async () => {
        if (!current_design || !loggedIn || !user_uuid) {
            return;
        }

        const qty = Math.max(1, Math.min(999, Number(orderQty) || 1));
        setOrderQty(qty);

        const panelsPayload = current_design.panels.map((panel) => ({
            id: panel.id,
            images: panel.images.map((image) => ({
                id: image.id,
                x_position: image.x_position,
                y_position: image.y_position,
                scale: image.scale,
                rotation: image.rotation,
                source_user_image_id: image.source_user_image_id || null,
                width: image.images_data.width || 0,
                height: image.images_data.height || 0,
            })),
        }));

        setCreatingOrder(true);
        try {
            const productImageData = await getPreviewImageData();
            const response = await HandleAxiosPromise({
                url: GEARSTUDIO_CREATE_ORDER_URL,
                method: "post",
                data: {
                    design_id: current_design.id,
                    qty,
                    panels: panelsPayload,
                    product_image_data: productImageData,
                },
                additional_headers: { "User-Id": user_uuid },
            });

            const paymentUrl = response?.data?.payment_url || response?.data?.data?.payment_url;
            if (paymentUrl) {
                window.location.href = String(paymentUrl);
                return;
            }

            setTextData({ text: "Order created but payment link was missing.", variant: "warning" });
        } catch (error) {
            const message = typeof error === "string" ? error : "Failed to create order.";
            setTextData({ text: message, variant: "danger" });
        } finally {
            setCreatingOrder(false);
        }
    };

    const showEditor = showImageEditor && !!livePanel;
    const isMobileEditorMode = isMobile && showEditor;

    if (!loggedIn) {
        return (
            <div className="mx-auto max-w-xl">
                <div className="gs-surface p-6">
                    <p className="gs-secondary mb-4 text-sm">Sign in to open and edit your designs.</p>
                    <GearstudioAuthPanel formOnly />
                </div>
            </div>
        );
    }

    return (
        <div className={`grid w-full items-start gap-6 ${isMobileEditorMode ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-[380px_minmax(0,1fr)] xl:gap-8"}`}>
            <aside className={`w-full lg:sticky lg:top-6 ${isMobileEditorMode ? "hidden" : ""}`}>
                <div className="gs-surface overflow-hidden">
                    <div className="space-y-3 p-6">
                        <Link
                            to="/"
                            className="gs-secondary inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] transition-colors hover:text-[var(--gs-text-primary)]"
                        >
                            <FiArrowLeft className="h-4 w-4" aria-hidden="true" />
                            Back to Studio
                        </Link>
                        <h2 className="gs-primary font-serif text-3xl leading-tight">Edit Design</h2>
                        <p className="gs-secondary text-sm">
                            Refine prompt, manage images, and edit image placement for this revision set.
                        </p>

                        <div className="mt-4">
                            <CountdownTimer usage={usageData} variant="gearstudio" />
                        </div>
                    </div>

                    <form
                        className="gs-divider-top space-y-4 bg-[color:var(--gs-bg-surface-alt)] p-6"
                        onSubmit={handleSubmitNewPrompt}
                    >
                        <div className="space-y-2">
                            <label htmlFor="revision_selector" className="gs-secondary text-sm font-semibold">
                                Revision
                            </label>
                            <select
                                id="revision_selector"
                                className={SELECT_CONTROL_CLASSNAME}
                                value={selectedDesignId || ""}
                                onChange={handleRevisionChange}
                                disabled={loadingDesign || sendingRequest || designs.length === 0}
                            >
                                {designs.length === 0 ? (
                                    <option value="">No revisions found</option>
                                ) : (
                                    designs.map((revision) => (
                                        <option key={revision.id} value={revision.id}>
                                            Rev {revisionDisplayNumberById.get(revision.id) || revision.revision_number} · {formatCreatedAt(revision.created_at, false)}
                                        </option>
                                    ))
                                )}
                            </select>
                        </div>

                        {current_design && current_design.panels.length > 1 ? (
                            <div className="space-y-2">
                                <label htmlFor="panel_selector" className="gs-secondary text-sm font-semibold">
                                    Panel
                                </label>
                                <select
                                    id="panel_selector"
                                    className={SELECT_CONTROL_CLASSNAME}
                                    value={livePanel?.id || ""}
                                    onChange={handlePanelChange}
                                    disabled={!current_design || current_design.panels.length === 0}
                                >
                                    {current_design?.panels.map((panel) => (
                                        <option key={panel.id} value={panel.id}>
                                            {panel.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ) : null}

                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                className="gs-btn-primary px-3 py-2 text-xs font-semibold"
                                onClick={() => setShowImageManager(true)}
                                disabled={!livePanel}
                            >
                                Manage Images
                            </button>
                            <button
                                type="button"
                                className="gs-btn-primary px-3 py-2 text-xs font-semibold"
                                onClick={() => setShowImageEditor((previous) => !previous)}
                                disabled={!livePanel}
                            >
                                {showImageEditor ? "Hide Editor" : "Image Editor"}
                            </button>
                        </div>

                        <p className="gs-muted text-xs">
                            Image Editor allows you to add additional images to the design and adjust their position, scale, and rotation on the design.
                        </p>

                        <div className="space-y-2">
                            <label className="gs-secondary text-sm font-semibold">Original Prompt</label>
                            <p className="gs-secondary min-h-[92px] rounded-[var(--gs-radius-input)] border border-[color:var(--gs-border-subtle)] bg-[color:var(--gs-input-bg)] px-3 py-2 text-sm">
                                {current_design?.user_prompt || "No prompt saved for this design."}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="edit_design_prompt" className="gs-secondary text-sm font-semibold">
                                New Prompt
                            </label>
                            <textarea
                                id="edit_design_prompt"
                                value={newPrompt}
                                onChange={(event) => setNewPrompt(event.target.value)}
                                disabled={!current_design || loadingDesign || sendingRequest}
                                required
                                className={TEXTAREA_CLASSNAME}
                                placeholder="Example: preserve the base style but use matte black with orange geometry and sharper contrast."
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={!current_design || !newPrompt.trim() || sendingRequest}
                            className="gs-btn-primary w-full px-4 py-3 text-sm"
                        >
                            {sendingRequest ? (
                                <span className="flex items-center justify-center gap-2">
                                    <FiLoader className="h-5 w-5 animate-spin" aria-hidden="true" />
                                    Sending Request...
                                </span>
                            ) : (
                                "Generate New Version"
                            )}
                        </button>

                        {current_design ? (
                            <div className="rounded-[var(--gs-radius-input)] border border-[color:var(--gs-border-subtle)] bg-[color:var(--gs-input-bg)] px-3 py-3">
                                <div className="flex items-center justify-between gap-4">
                                    <span className="gs-secondary text-xs font-semibold">
                                        Product Subtotal ({Math.max(1, Number(orderQty) || 1)}x)
                                    </span>
                                    <span className="gs-primary text-sm font-semibold">
                                        {USD_CURRENCY_FORMATTER.format(pricingSummary.subtotal)}
                                    </span>
                                </div>
                                <div className="mt-2 flex items-center justify-between gap-4">
                                    <span className="gs-secondary text-xs font-semibold">Processing Fee</span>
                                    <span className="gs-primary text-sm font-semibold">
                                        {USD_CURRENCY_FORMATTER.format(pricingSummary.processingFee)}
                                    </span>
                                </div>
                                <div className="mt-2 border-t border-[color:var(--gs-border-subtle)] pt-2 flex items-center justify-between gap-4">
                                    <span className="gs-primary text-xs font-semibold uppercase tracking-[0.1em]">
                                        Total
                                    </span>
                                    <span className="gs-primary text-base font-bold">
                                        {USD_CURRENCY_FORMATTER.format(pricingSummary.total)}
                                    </span>
                                </div>
                            </div>
                        ) : null}

                        {current_design ? (
                            <div className="rounded-[var(--gs-radius-input)] border border-[color:var(--gs-border-subtle)] bg-[color:var(--gs-input-bg)] px-3 py-3">
                                <div className="flex items-center justify-between gap-4">
                                    <span className="gs-secondary text-xs font-semibold uppercase tracking-[0.1em]">
                                        Standard Delivery ETA
                                    </span>
                                    <span className="gs-primary text-sm font-semibold">
                                        {loadingStandardDeliveryDate
                                            ? "Checking..."
                                            : standardDeliveryDate
                                                ? formatDeliveryDate(standardDeliveryDate)
                                                : "Unavailable"}
                                    </span>
                                </div>
                            </div>
                        ) : null}

                        <div className="grid grid-cols-[1fr_auto] items-end gap-3">
                            <label className="space-y-2">
                                <span className="gs-secondary text-sm font-semibold">Qty</span>
                                <input
                                    type="number"
                                    min={1}
                                    max={99}
                                    value={orderQty}
                                    onChange={(event) => {
                                        const nextQty = Number(event.target.value);
                                        if (Number.isNaN(nextQty)) {
                                            setOrderQty(1);
                                            return;
                                        }
                                        setOrderQty(Math.max(1, Math.min(999, nextQty)));
                                    }}
                                    disabled={!current_design || creatingOrder}
                                    className={SELECT_CONTROL_CLASSNAME + " max-w-[80px]"}
                                />
                            </label>
                            <button
                                type="button"
                                onClick={handleCreateOrder}
                                disabled={!current_design || creatingOrder || sendingRequest || loadingDesign}
                                className="gs-btn-primary px-4 py-3 text-sm whitespace-nowrap"
                            >
                                {creatingOrder ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <FiLoader className="h-5 w-5 animate-spin" aria-hidden="true" />
                                        Creating...
                                    </span>
                                ) : (
                                    "Create Order"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </aside>

            <section
                ref={previewContainerRef}
                className={
                    isMobileEditorMode
                        ? "fixed inset-0 z-[90] flex flex-col bg-[color:var(--gs-bg-surface)]"
                        : isMobile
                            ? "gs-surface overflow-hidden"
                            : "gs-surface space-y-5 p-6 sm:p-8"
                }
            >
                {!isMobile ? (
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <p className="gs-muted font-mono text-[11px] uppercase tracking-[0.14em]">
                                Design Preview
                            </p>
                            <h3 className="gs-primary font-serif text-3xl">
                                {current_design?.name || (designId ? `Design #${designId}` : "Design")}
                            </h3>
                        </div>
                        <p className="gs-muted text-xs">
                            {current_design?.created_at ? `Created ${formatCreatedAt(current_design.created_at)}` : ""}
                        </p>
                    </div>
                ) : null}

                {loadingDesign ? (
                    <div className={`gs-surface-alt flex flex-col items-center justify-center px-6 text-center ${isMobile ? "h-[420px]" : "min-h-[420px]"}`}>
                        <FiLoader className="h-10 w-10 animate-spin text-[var(--gs-accent-primary)]" aria-hidden="true" />
                        <p className="gs-secondary mt-3 text-sm">Loading design data...</p>
                    </div>
                ) : current_design?.product_template?.gltf_file ? (
                    <div className={isMobileEditorMode ? "h-full min-h-0" : "space-y-4"}>
                        {showEditor ? (
                            <div className={`${isMobileEditorMode ? "flex h-full min-h-0 flex-col" : "flex h-[calc(100svh-16rem)] min-h-[540px] flex-col gap-4 sm:h-[calc(100svh-15rem)] lg:h-[calc(100vh-12rem)] lg:min-h-[700px]"}`}>
                                <div className={`overflow-hidden rounded-[var(--gs-radius-card)] border border-[color:var(--gs-border-subtle)] bg-[color:var(--gs-bg-surface-alt)] ${isMobile ? "h-1/2" : "h-1/2 lg:h-[65%]"}`}>
                                    <AiDesignStudioWrapper
                                        ref={studioRef}
                                        gltf_file={current_design.product_template as any}
                                        panels={current_design.panels}
                                        preserve_drawing_buffer={true}
                                    />
                                </div>
                                <ImageEditorSection
                                    livePanel={livePanel}
                                    setLivePanel={updateLivePanel}
                                    selectedImageId={selectedImageId}
                                    setSelectedImageId={setSelectedImageId}
                                    setEditImages={setShowImageEditor}
                                    user_uuid={user_uuid}
                                    isMobile={isMobile}
                                />
                            </div>
                        ) : (
                            <div className="h-[360px] overflow-hidden rounded-[var(--gs-radius-card)] border border-[color:var(--gs-border-subtle)] bg-[color:var(--gs-bg-surface-alt)] sm:h-[460px] lg:h-[620px]">
                                <AiDesignStudioWrapper
                                    ref={studioRef}
                                    gltf_file={current_design.product_template as any}
                                    panels={current_design.panels}
                                    preserve_drawing_buffer={true}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="rounded-[var(--gs-radius-input)] border border-[color:rgba(201,107,59,0.45)] bg-[color:rgba(201,107,59,0.12)] p-4 text-sm text-[var(--gs-text-primary)]">
                        Missing 3D model data for this design. Cannot display preview.
                    </div>
                )}
            </section>

            <ImageManagerModal
                open={showImageManager}
                user_uuid={user_uuid}
                onClose={() => setShowImageManager(false)}
                onAddImageToPanel={handleAddImageToPanel}
            />
        </div>
    );
};

type ImageEditorSectionProps = {
    livePanel: GearStudioPanel;
    setLivePanel: (updater: (panel: GearStudioPanel) => GearStudioPanel) => void;
    selectedImageId: number | null;
    setSelectedImageId: React.Dispatch<React.SetStateAction<number | null>>;
    setEditImages: React.Dispatch<React.SetStateAction<boolean>>;
    user_uuid: string;
    isMobile: boolean;
};

const ImageEditorSection = ({
    livePanel,
    setLivePanel,
    selectedImageId,
    setSelectedImageId,
    setEditImages,
    user_uuid,
    isMobile
}: ImageEditorSectionProps) => {
    const { setTextData } = useMessage();
    const { globalLoading, setGlobalLoading } = useGearStudioDesign();
    const [saveLoading, setSaveLoading] = useState(false);
    const [workingPanel, setWorkingPanelState] = useState<GearStudioPanel>(livePanel);
    const logoEditBaselineRef = useRef<{ panelId: number; signature: string } | null>(null);
    const workingPanelRef = useRef<GearStudioPanel>(livePanel);
    const editorSectionRef = useRef<HTMLDivElement | null>(null);

    const getLogoEditSignature = useCallback((panel: GearStudioPanel) => (
        JSON.stringify(
            panel.images
                .map((image) => ({
                    id: image.id,
                    x_position: image.x_position,
                    y_position: image.y_position,
                    scale: image.scale,
                    rotation: image.rotation,
                }))
                .sort((a, b) => a.id - b.id)
        )
    ), []);

    const setWorkingPanel = useCallback((updater: (panel: GearStudioPanel) => GearStudioPanel) => {
        setWorkingPanelState((previous) => {
            const next = updater(previous);
            workingPanelRef.current = next;
            return next;
        });
    }, []);

    useEffect(() => {
        setWorkingPanelState(livePanel);
        workingPanelRef.current = livePanel;
        logoEditBaselineRef.current = {
            panelId: livePanel.id,
            signature: getLogoEditSignature(livePanel),
        };

        return () => {
            logoEditBaselineRef.current = null;
        };
    }, [livePanel.id, getLogoEditSignature]);

    useEffect(() => {
        const baseline = logoEditBaselineRef.current;
        if (!baseline || baseline.panelId !== livePanel.id) {
            return;
        }
        const currentWorkingPanel = workingPanelRef.current;
        const workingSignature = getLogoEditSignature(currentWorkingPanel);
        if (workingSignature !== baseline.signature) {
            // Preserve unsaved local edits, but merge in newly added images from the latest live panel.
            const existingImageIds = new Set(currentWorkingPanel.images.map((image) => image.id));
            const incomingImages = livePanel.images.filter((image) => !existingImageIds.has(image.id));
            if (incomingImages.length === 0) {
                return;
            }

            const mergedPanel: GearStudioPanel = {
                ...livePanel,
                images: [...currentWorkingPanel.images, ...incomingImages],
            };
            setWorkingPanelState(mergedPanel);
            workingPanelRef.current = mergedPanel;
            return;
        }
        setWorkingPanelState(livePanel);
        workingPanelRef.current = livePanel;
        logoEditBaselineRef.current = {
            panelId: livePanel.id,
            signature: getLogoEditSignature(livePanel),
        };
    }, [livePanel, getLogoEditSignature]);

    const handleLiveImageUpdate = useCallback((image_id: number, properties: Partial<GearStudioImage>) => {
        setWorkingPanel((prevPanel) => {
            let changed = false;
            const updatedImages = prevPanel.images.map((image) => {
                if (image.id !== image_id) {
                    return image;
                }
                const updatedImage = { ...image, ...properties };
                const nextChanged = Object.entries(properties).some(([key, value]) => {
                    return (image as Record<string, unknown>)[key] !== value;
                });
                if (nextChanged) {
                    changed = true;
                }
                return nextChanged ? updatedImage : image;
            });
            if (!changed) {
                return prevPanel;
            }
            return { ...prevPanel, images: updatedImages };
        });
    }, [setWorkingPanel]);

    const handleInteractionEnd = useCallback(() => {
        const currentWorkingPanel = workingPanelRef.current;
        if (currentWorkingPanel.id !== livePanel.id) {
            return;
        }
        if (getLogoEditSignature(currentWorkingPanel) === getLogoEditSignature(livePanel)) {
            return;
        }
        setLivePanel(() => currentWorkingPanel);
    }, [getLogoEditSignature, livePanel, setLivePanel]);

    const handleDeleteImage = async (image_id: number) => {
        const confirmed = window.confirm("Delete this image from the panel? This cannot be undone.");
        if (!confirmed) {
            return;
        }

        setGlobalLoading(true);
        try {
            const response = await HandleAxiosPromise({
                url: GEARSTUDIO_PANEL_IMAGES_URL,
                method: "delete",
                data: { panel_id: workingPanel.id, image_id },
                additional_headers: { "User-Id": user_uuid },
            });
            if (response.status === 200) {
                const removeImage = (prevPanel: GearStudioPanel) => {
                    const updatedImages = prevPanel.images.filter((image) => image.id !== image_id);
                    return { ...prevPanel, images: updatedImages };
                };
                setWorkingPanel(removeImage);
                setLivePanel(removeImage);
                setTextData({ text: "Image deleted successfully.", variant: "success" });
                setSelectedImageId(null);
            } else {
                setTextData({ text: "Failed to delete image.", variant: "danger" });
            }
        } catch (error) {
            console.error(error);
            setTextData({ text: "Error deleting image.", variant: "danger" });
        } finally {
            setGlobalLoading(false);
        }
    };

    const hasPendingChanges = useMemo(() => {
        const baseline = logoEditBaselineRef.current;
        if (!baseline || baseline.panelId !== workingPanel.id) {
            return true;
        }
        const currentSignature = getLogoEditSignature(workingPanel);
        const hasUnsavedLocalImages = workingPanel.images.some((image) => image.id <= 0);
        return baseline.signature !== currentSignature || hasUnsavedLocalImages;
    }, [getLogoEditSignature, workingPanel]);

    const saveImageEdits = useCallback(async (closeAfterSave: boolean) => {
        const currentWorkingPanel = workingPanelRef.current;
        const baseline = logoEditBaselineRef.current;
        const currentSignature = getLogoEditSignature(currentWorkingPanel);
        const hasUnsavedLocalImages = currentWorkingPanel.images.some((image) => image.id <= 0);
        const hasChanges = !(
            baseline
            && baseline.panelId === currentWorkingPanel.id
            && baseline.signature === currentSignature
            && !hasUnsavedLocalImages
        );

        if (!hasChanges) {
            if (closeAfterSave) {
                setEditImages(false);
                setSelectedImageId(null);
            } else {
                setTextData({ text: "No changes to save.", variant: "info" });
            }
            return;
        }

        const payload = {
            panel_id: currentWorkingPanel.id,
            images: currentWorkingPanel.images.map((image) => ({
                id: image.id,
                x_position: image.x_position,
                y_position: image.y_position,
                scale: image.scale,
                rotation: image.rotation,
                source_user_image_id: image.source_user_image_id || null,
                width: image.images_data.width || 0,
                height: image.images_data.height || 0,
            })),
        };

        setSaveLoading(true);
        setGlobalLoading(true);
        try {
            const response = await HandleAxiosPromise({
                url: GEARSTUDIO_PANEL_IMAGES_URL,
                method: "patch",
                data: payload,
                additional_headers: { "User-Id": user_uuid },
            });
            if (response.status === 200 && response.data) {
                const updatedPanel = response.data as GearStudioPanel;
                setWorkingPanel(() => updatedPanel);
                setLivePanel(() => updatedPanel);
                logoEditBaselineRef.current = {
                    panelId: updatedPanel.id,
                    signature: getLogoEditSignature(updatedPanel),
                };
                if (closeAfterSave) {
                    setEditImages(false);
                    setSelectedImageId(null);
                } else {
                    setTextData({ text: "Image edits saved.", variant: "success" });
                }
            } else {
                setTextData({ text: "Failed to save image edits.", variant: "danger" });
            }
        } catch (error) {
            console.error(error);
            setTextData({ text: "Error saving image edits.", variant: "danger" });
        } finally {
            setSaveLoading(false);
            setGlobalLoading(false);
        }
    }, [getLogoEditSignature, setEditImages, setGlobalLoading, setLivePanel, setSelectedImageId, setTextData, setWorkingPanel, user_uuid]);

    const handleCloseImageEdit = async () => {
        await saveImageEdits(true);
    };

    return (
        <div
            ref={editorSectionRef}
            className={`relative z-40 flex min-h-0 w-full flex-col border-t-2 border-orange-500 bg-white shadow-[0_-5px_15px_rgba(0,0,0,0.1)] ${isMobile ? "h-1/2" : "h-1/2 lg:h-[35%]"}`}
        >
            <div className="flex items-center justify-between pt-2 px-2 border-b border-gray-100 bg-gray-50 flex-shrink-0 flex-wrap">
                <div className="flex flex-row items-center gap-2">
                    <h4 className="font-bold text-gray-600 text-sm">Positioning Image on: <span className="text-orange-600">{workingPanel.name}</span></h4>
                    <TooltipElement text="This is a 2D representation of the product. Select and move images, then adjust scale and rotation as needed." />
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => {
                            if (selectedImageId) {
                                handleDeleteImage(selectedImageId);
                            }
                        }}
                        disabled={!selectedImageId || globalLoading}
                        className={`flex h-7 w-7 items-center justify-center rounded bg-black text-white shadow-sm transition-colors hover:bg-gray-800 cursor-pointer ${!selectedImageId ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                        title="Delete image"
                    >
                        <FiTrash2 className="h-5 w-5" aria-hidden="true" />
                        <span className="sr-only">Delete image</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => saveImageEdits(false)}
                        disabled={saveLoading || globalLoading || !hasPendingChanges}
                        className={`flex h-7 w-7 items-center justify-center rounded bg-black text-white shadow-sm transition-colors hover:bg-gray-800 cursor-pointer ${saveLoading || globalLoading || !hasPendingChanges ? "opacity-50 cursor-not-allowed" : ""}`}
                        title="Save edits"
                    >
                        <FiSave className="h-5 w-5" aria-hidden="true" />
                        <span className="sr-only">Save image edits</span>
                    </button>
                    <button
                        onClick={() => handleCloseImageEdit()}
                        disabled={saveLoading || globalLoading}
                        className="flex h-7 w-7 items-center justify-center rounded bg-black text-white shadow-sm transition-colors hover:bg-gray-800 cursor-pointer"
                    >
                        <FaWindowClose className="w-5 h-5" aria-hidden="true" />
                        <span className="sr-only">Done / Close Editor</span>

                    </button>
                </div>
            </div>
            {workingPanel.images.length === 0 ? (
                <div className="border-b border-orange-100 bg-orange-50 px-3 py-2">
                    <p className="text-xs font-medium text-orange-700">
                        This panel has no images yet. Please use the <strong>Manage Images</strong> button to add an image, then position it here.
                    </p>
                </div>
            ) : null}
            <div className="flex-1 overflow-y-auto p-4 flex justify-center bg-gray-100">
                <CanvasToolbarProvider>
                    <LogoEditor2D
                        disabled={globalLoading}
                        panel_id={workingPanel.id}
                        panel_image={workingPanel.images_data.webp || workingPanel.images_data.src || ""}
                        panel_images={workingPanel.images}
                        selectedImageId={selectedImageId}
                        onSelectImage={setSelectedImageId}
                        maxCssWidth={1200} // Slightly reduced to fit split view better
                        aspect={`${workingPanel.images_data.width || 0}/${workingPanel.images_data.height || 0}`}
                        background="#FFFFFF"
                        modalAnchorRef={editorSectionRef}
                        onLiveImageUpdate={handleLiveImageUpdate}
                        onInteractionEnd={handleInteractionEnd}
                    />
                </CanvasToolbarProvider>
            </div>
        </div>
    );
};
